import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    TextField,
} from '@mui/material';
import { createContext, ReactNode, useContext, useState } from 'react';

export interface ConfirmationProps {
    onConfirm: () => void;
    onCancel?: () => void;
    open: boolean;
    isLoading: boolean;
    confirmTitle?: string;
    cancelText?: string;
    confirmText?: string;
}

const confirmTextDefaultValue = {
    confirm: (_text: string, _requireReason?: boolean, _reasonText?: string) =>
        new Promise<string>(() => ''),
};

export const ConfirmContext = createContext(confirmTextDefaultValue);

export type ConfirmContextWrapperProps = {
    children: ReactNode;
};

export function ConfirmContextWrapper({
    children,
}: ConfirmContextWrapperProps) {
    /* This wrapper creates a promise stored in context.
     * This promise is rejected if the the user does not accept the action
     * This promise is accepted if the user accepts the action.
     * This promise is accessed through the useConfirm hook
     * This promise can is used as a normal promise and is best used with .then and .catch
     * eg. const delete = () => acceptedPromise.then(() => accept).reject(() => rejected)
     */

    const [confirmText, setConfirmText] = useState('');

    const [getReasonForChangeText, setGetReasonForChangeText] = useState<
        string | null | undefined
    >('Give a short description for why this is being deleted.');

    const [shouldRequireReason, setRequireReason] = useState(false);

    const [resolveReject, setResolveReject] = useState<
        ((text: string) => void)[]
    >([]);
    const [resolvePromise, rejectPromise] = resolveReject;

    const [isOpen, setIsOpen] = useState(false);

    const [reasonForChange, setReasonForChange] = useState<string>('');

    const confirm = (
        text: string,
        requireReason?: boolean,
        reasonText?: string,
    ) => {
        setConfirmText(text);
        setIsOpen(true);
        setRequireReason(requireReason ?? false);
        setGetReasonForChangeText(
            reasonText ??
                'Give a short description for why this is being deleted.',
        );
        return new Promise<string>((resolve, reject) => {
            setResolveReject([resolve, reject]);
        });
    };

    function handleCancel() {
        setIsOpen(false);
        setReasonForChange('');
        rejectPromise(`The action ${confirmText} was canceled`);
    }

    function handleAccept() {
        setIsOpen(false);
        setReasonForChange('');
        resolvePromise(
            shouldRequireReason
                ? reasonForChange
                : `The action ${confirmText} was confirmed`,
        );
    }

    return (
        <ConfirmContext.Provider
            value={{
                confirm,
            }}
        >
            <Dialog open={isOpen}>
                <DialogTitle>Are you sure</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        {confirmText}
                    </Typography>

                    {shouldRequireReason && (
                        <>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                {getReasonForChangeText}
                            </Typography>
                            <TextField
                                value={reasonForChange || ''}
                                onChange={(e) => {
                                    setReasonForChange(e.target.value);
                                }}
                                placeholder="Enter a Reason"
                                fullWidth
                                multiline
                                minRows={2}
                                required
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        color="warning"
                        autoFocus
                        onClick={handleCancel}
                    >
                        No
                    </Button>
                    <Button
                        color="success"
                        onClick={handleAccept}
                        disabled={shouldRequireReason && !reasonForChange}
                    >
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
            {children}
        </ConfirmContext.Provider>
    );
}

export function useConfirm() {
    const { confirm } = useContext(ConfirmContext);

    return confirm;
}
